import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import type { RefetchFunction } from '@apollo/client/react/internal'
import {
  CLAIM_COLLECTIBLE_FRAME_MUTATION,
  CLAIM_COLLECTIBLE_FRAMES_MUTATION,
  GET_USER_COLLECTIBLES,
} from '@/services/NftService/querys'
import {
  ClaimCollectibleFrameInput,
  GetUserCollectiblesQuery,
  GetUserCollectiblesQueryVariables,
} from '@/types/codegen-federation'

export type OwnedFramesList = NonNullable<{
  niftys: NonNullable<
    NonNullable<
      NonNullable<NonNullable<NonNullable<GetUserCollectiblesQuery>['user']>['wallet']>['niftyList']
    >['niftys']
  >
  count: NonNullable<
    NonNullable<NonNullable<NonNullable<NonNullable<GetUserCollectiblesQuery>['user']>['wallet']>['niftyList']>['count']
  >
}>

export type OwnedNifty = NonNullable<
  NonNullable<NonNullable<NonNullable<NonNullable<GetUserCollectiblesQuery>['user']>['wallet']>['niftyList']>['niftys']
>[number]

export type OwnedFrame = NonNullable<NonNullable<NonNullable<OwnedNifty>['frames']>[number]>
export type OwnedAsset = NonNullable<NonNullable<NonNullable<OwnedNifty>['images']>[number]>

export const useUserCollectibles = (): {
  frames: OwnedFramesList
  claimedFrames: OwnedFramesList['niftys']
  signedClaimedFrames: OwnedFramesList['niftys']
  isLoading: boolean
  unclaimedFrames: OwnedFramesList['niftys']
  promotionalNiftys: OwnedFramesList['niftys']
  otherNiftys: OwnedFramesList['niftys']
  refetchUserCollectibles: RefetchFunction<GetUserCollectiblesQuery, GetUserCollectiblesQueryVariables>
} => {
  const { data, loading, refetch } = useQuery<GetUserCollectiblesQuery, GetUserCollectiblesQueryVariables>(
    GET_USER_COLLECTIBLES,
    {
      errorPolicy: 'all',
    },
  )

  const sortBySignature = (claimedMoments: OwnedFramesList['niftys']) => {
    const filteredMoments = claimedMoments.filter((moment) => moment !== null)

    return filteredMoments.toSorted((a, b) => {
      const hasSignatureA = (a?.signatures ?? []).some((signature) => signature?.signatureUrl !== null)
      const hasSignatureB = (b?.signatures ?? []).some((signature) => signature?.signatureUrl !== null)

      if (hasSignatureA && !hasSignatureB) {
        return -1
      }
      if (!hasSignatureA && hasSignatureB) {
        return 1
      }
      return 0
    })
  }

  return useMemo(() => {
    const frames = {
      niftys: data?.user?.wallet?.niftyList?.niftys ?? [],
      count: data?.user?.wallet?.niftyList?.count ?? 0,
    }

    const unclaimedFrames = frames.niftys?.filter(
      (n) =>
        !n?.claimedStatus?.claimedAt &&
        n?.claimedStatus?.claimToken &&
        !n?.frames?.find((i) => i?.episode?.show?.projectId === 'the-chosen'),
    )

    const sortedClaimedFrames = frames.niftys?.filter(
      (n) =>
        n?.claimedStatus?.claimedAt ||
        (!n?.claimedStatus?.id && !n?.frames?.find((i) => i?.episode?.show?.projectId === 'the-chosen')),
    )
    sortedClaimedFrames?.sort((a, b) => (b?.claimedStatus?.claimedAt || 0) - (a?.claimedStatus?.claimedAt || 0))

    const unsortedSignedClaimedFrames = sortedClaimedFrames.filter((claimedFrame) => {
      return (
        (claimedFrame?.signatures?.length ?? 0) > 0 &&
        claimedFrame?.frames?.find((i) => i?.episode?.show?.projectId !== 'the-chosen')
      )
    })

    const signedClaimedFrames = sortBySignature(unsortedSignedClaimedFrames)

    const unsignedClaimedFrames = sortedClaimedFrames?.filter(
      (f) => f?.signatures?.length === 0 && f.frames?.find((i) => i?.episode?.show?.projectId !== 'the-chosen'),
    )

    const promotionalNiftys =
      frames.niftys?.filter(
        (n) =>
          (n?.frames?.length ?? 0) === 0 && (n?.images?.length ?? 0) > 0 && n?.contract?.[0]?.projectId === 'sight',
      ) ?? []

    const otherNiftys = frames.niftys?.filter(
      (n) => n?.images && n.images?.length > 0 && n?.contract?.[0]?.projectId !== 'sight',
    )

    return {
      frames,
      unclaimedFrames,
      claimedFrames: unsignedClaimedFrames,
      signedClaimedFrames,
      promotionalNiftys,
      isLoading: loading,
      otherNiftys,
      refetchUserCollectibles: refetch,
    }
  }, [data?.user?.wallet?.niftyList?.count, data?.user?.wallet?.niftyList?.niftys, loading, refetch])
}

export const useClaimCollectibleFrames = () => {
  const [claimCollectibleFramesMutation, { data: claimFramesData, loading: claimFramesIsLoading }] = useMutation(
    CLAIM_COLLECTIBLE_FRAMES_MUTATION,
    {
      errorPolicy: 'all',
    },
  )

  const claimCollectibleFrames = useCallback(async () => {
    return claimCollectibleFramesMutation({
      refetchQueries: [{ query: GET_USER_COLLECTIBLES }, 'getUserCollectibles'],
      errorPolicy: 'all',
    })
  }, [claimCollectibleFramesMutation])

  return useMemo(() => {
    return { claimCollectibleFrames, claimFramesData, claimFramesIsLoading }
  }, [claimCollectibleFrames, claimFramesData, claimFramesIsLoading])
}

export const useClaimCollectibleFrame = () => {
  const [claimCollectibleFrameMutation, { data: claimFrameData, loading: claimFrameIsLoading }] = useMutation(
    CLAIM_COLLECTIBLE_FRAME_MUTATION,
    {
      errorPolicy: 'all',
    },
  )

  const claimCollectibleFrame = useCallback(
    async ({ id }: ClaimCollectibleFrameInput) => {
      return claimCollectibleFrameMutation({
        refetchQueries: [{ query: GET_USER_COLLECTIBLES }, 'getUserCollectibles'],
        variables: {
          input: {
            id,
          },
        },
        errorPolicy: 'all',
      })
    },
    [claimCollectibleFrameMutation],
  )
  return useMemo(() => {
    return { claimCollectibleFrame, claimFrameIsLoading, claimFrameData }
  }, [claimCollectibleFrame, claimFrameData, claimFrameIsLoading])
}
